import { Button, Dialog, DialogProps, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from 'react-i18next';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import React from 'react';
import { TFunctionResult } from 'i18next';

import LoadingButton from '../LoadingButton';
import { ExtendedTheme } from '../../config/appTheme';

import {
  DialogImage,
  DialogContainer,
  DialogActionsStyled,
  DialogContentStyled,
  DialogTitleStyled,
  DialogText,
  CloseButton,
} from './ActionDialog.parts';

const useDialogStyles = makeStyles<ExtendedTheme, { allowOverflow: boolean }>(() => ({
  paper: { overflowY: ({ allowOverflow }) => (allowOverflow ? 'visible' : 'auto') },
}));

type ActionDialogMaxWidth = DialogProps['maxWidth'];

interface ActionDialogProps {
  content: string | number | boolean | React.ReactElement | TFunctionResult;
  open: boolean;
  buttonDisabled?: boolean;
  fullLengthButton?: boolean;
  justifyRight?: boolean;
  disableBackdropClick?: boolean;
  confirmationButtonText?: string | number | React.ReactElement | TFunctionResult;
  hideCancel?: boolean;
  image?: React.ReactElement;
  showCloseIcon?: boolean;
  closeBtnText?: string;
  title?: string | React.ReactElement | TFunctionResult;
  isLoading?: boolean;
  maxWidth?: ActionDialogMaxWidth;
  disableFullscreen?: boolean;
  allowOverflow?: boolean;
  handleConfirmation?: (event: React.MouseEvent) => void;
  handleClose?: (event: React.MouseEvent) => void;
  noActions?: boolean;
  scroll?: DialogProps['scroll'];
}

const defaultProps = {
  buttonDisabled: false,
  fullLengthButton: true,
  justifyRight: false,
  disableBackdropClick: false,
  confirmationButtonText: undefined,
  hideCancel: false,
  image: undefined,
  showCloseIcon: false,
  closeBtnText: '',
  title: undefined,
  isLoading: false,
  maxWidth: 'sm' as ActionDialogMaxWidth,
  disableFullscreen: false,
  allowOverflow: false,
  handleConfirmation: undefined,
  handleClose: undefined,
  noActions: false,
  scroll: undefined,
};

export default function ActionDialog({
  content,
  open,
  buttonDisabled = defaultProps.buttonDisabled,
  fullLengthButton = defaultProps.fullLengthButton,
  justifyRight = defaultProps.justifyRight,
  confirmationButtonText = defaultProps.confirmationButtonText,
  disableBackdropClick = defaultProps.disableBackdropClick,
  hideCancel = defaultProps.hideCancel,
  image = defaultProps.image,
  showCloseIcon = defaultProps.showCloseIcon,
  isLoading = defaultProps.isLoading,
  maxWidth = defaultProps.maxWidth,
  closeBtnText = defaultProps.closeBtnText,
  title = defaultProps.title,
  disableFullscreen = defaultProps.disableFullscreen,
  allowOverflow = defaultProps.allowOverflow,
  handleConfirmation = defaultProps.handleConfirmation,
  handleClose,
  noActions,
  scroll,
}: ActionDialogProps) {
  const { t } = useTranslation();

  const dialogClasses = useDialogStyles({ allowOverflow });
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      disableBackdropClick={disableBackdropClick}
      fullScreen={!disableFullscreen && fullScreen}
      maxWidth={maxWidth}
      open={open}
      onClose={handleClose}
      aria-labelledby="action-dialog"
      data-testid="action-dialog-wrapper"
      classes={dialogClasses}
      scroll={scroll}
    >
      <DialogContainer large={maxWidth === 'lg' ? 1 : 0} allowOverflow={allowOverflow ? 1 : 0}>
        {title && (
          <DialogTitleStyled disableTypography>
            {image && <DialogImage>{image}</DialogImage>}
            <Typography variant="h3" color="primary">
              {title}
            </Typography>
          </DialogTitleStyled>
        )}
        {showCloseIcon && (
          <CloseButton onClick={handleClose} size="small">
            <CloseIcon />
          </CloseButton>
        )}
        <DialogContentStyled>
          <DialogText id="action-dialog-description">{content}</DialogText>
        </DialogContentStyled>
      </DialogContainer>
      {!noActions && (
        <DialogActionsStyled
          fulllength={+fullLengthButton}
          disableSpacing={fullLengthButton}
          justifyright={+justifyRight}
        >
          {!hideCancel && (
            <Button
              variant={closeBtnText ? 'outlined' : 'text'}
              size={fullLengthButton ? 'small' : 'medium'}
              color="primary"
              onClick={handleClose}
            >
              {closeBtnText || t('cancel')}
            </Button>
          )}
          <LoadingButton
            variant="contained"
            color="primary"
            disabled={buttonDisabled}
            isLoading={isLoading}
            onClick={handleConfirmation}
            data-testid="action-dialog-confirm"
          >
            {confirmationButtonText || t('ok')}
          </LoadingButton>
        </DialogActionsStyled>
      )}
    </Dialog>
  );
}

ActionDialog.defaultProps = defaultProps;
